import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Kind, Order, OrderStatus, OrderType } from "../../types/order.type";
import { api } from "../../api/api";
import useGetRequest from "../../hooks/useGetRequest";
import {
  Button,
  Col,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Typography,
  Card,
  Popconfirm,
  Checkbox,
  Popover,
} from "antd";
import { Currency } from "../../types/currency.type";
import { DownOutlined, SettingOutlined } from "@ant-design/icons";
import CreateOrder from "../../components/CreateOrder/createOrder.component";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import dayjs from "dayjs";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import CreateDeliveryExpense from "../../components/CreateDeliveryExpense/createDeliveryExpense.component";
import CreateFork from "../../components/CreateFork/createFork.component";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import { canEditOrders } from "../../utils/roleUtils";
import { useQueryParams } from "../../hooks/useQueryParams";
import { ITaskOrder, TaskOrderStatus } from "../../types/taskOrder.type";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

export default function Orders() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const { itemId, type, name, clientType } = useParams();
  const queryParams = useQueryParams();

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);

  // Инициализация состояний из URL query параметров
  const [searchTerm, setSearchTerm] = useState<string>(queryParams.getParam("search") || "");
  const [orderType, setOrderType] = useState<OrderType | undefined>(queryParams.getParam("orderType") as OrderType || undefined);
  const [kind, setKind] = useState<Kind | undefined>(queryParams.getParam("kind") as Kind || undefined);
  const [currency, setCurrency] = useState<Currency | undefined>(queryParams.getParam("currency") as Currency || undefined);
  const [sortBy, setSortBy] = useState<string>(queryParams.getParam("sortBy") || "date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">((queryParams.getParam("sortOrder") as "asc" | "desc") || "desc");
  const [page, setPage] = useState<number>(queryParams.getParamAsNumber("page", 1));
  const [pageSize, setPageSize] = useState<number>(queryParams.getParamAsNumber("pageSize", 10));
  const [total, setTotal] = useState<number>(0);
  const [status, setStatus] = useState<OrderStatus | undefined>(queryParams.getParam("status") as OrderStatus || undefined);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState<boolean>(false);
  const [stats, setStats] = useState<{
    totalProfit: { USD: number; TJS: number };
    totalReceived: { USD: number; TJS: number };
    totalOrderAmount: { USD: number; TJS: number };
    totalQuantity: number;
  }>();
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [reviewLoading, setReviewLoading] = useState<boolean>(false);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [orderForm] = Form.useForm();
  const { data: producesList } = useGetRequest("/produce/list");

  const [taskOrders, setTaskOrders] = useState<ITaskOrder[]>([]);
  const [taskOrdersLoading, setTaskOrdersLoading] = useState<boolean>(false);
  const [selectedTaskOrderId, setSelectedTaskOrderId] = useState<string | undefined>(undefined);
  const [isTaskOrderSelectModalOpen, setIsTaskOrderSelectModalOpen] = useState<boolean>(false);
  const [createOrderInitialValues, setCreateOrderInitialValues] = useState<Record<string, any>>();
  const [createOrderDisabledFields, setCreateOrderDisabledFields] = useState<string[]>([]);
  const [createOrderTotalMax, setCreateOrderTotalMax] = useState<number | undefined>(undefined);

  // Инициализация dateRange из URL
  const initDateRange = (): [dayjs.Dayjs, dayjs.Dayjs] | undefined => {
    const startDate = queryParams.getParam("startDate");
    const endDate = queryParams.getParam("endDate");
    if (startDate && endDate) {
      return [dayjs(startDate, "DD.MM.YYYY"), dayjs(endDate, "DD.MM.YYYY")];
    }
    return [dayjs().startOf("month"), dayjs().endOf("month")];
  };

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | undefined>(initDateRange());
  const [isDeliveryExpenseModalOpen, setIsDeliveryExpenseModalOpen] =
    useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [deliveryExpenseForm] = Form.useForm();
  const [isForkModalOpen, setIsForkModalOpen] = useState<boolean>(false);
  const [forkForm] = Form.useForm();

  const defaultVisibleColumns = {
    date: true,
    receipt: true,
    clientName: true,
    description: false,
    type: true,
    kind: true,
    name: true,
    unitOfMeasurement: true,
    currency: true,
    exchangeRate: true,
    discount: true,
    price: true,
    total: true,
    totalCost: true,
    balanceBefore: true,
    balanceAfter: true,
    residueBefore: true,
    residueAfter: true,
    returned: true,
    status: true,
    profit: true,
    ownerName: true,
    actions: true,
    totalKg: true,
  };

  const loadColumnVisibility = (): Record<string, boolean> => {
    try {
      const saved = localStorage.getItem('orders-column-visibility');
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...defaultVisibleColumns, ...parsed };
      }
    } catch (error) {
      console.warn('Failed to load column visibility from localStorage:', error);
    }
    return defaultVisibleColumns;
  };

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(loadColumnVisibility);

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
  };

  const handleExport = () => {
    const exportParams: { [key: string]: any } = {
      startDate: dateRange ? dateRange[0]?.format("DD.MM.YYYY") : undefined,
      endDate: dateRange ? dateRange[1]?.format("DD.MM.YYYY") : undefined,
    };

    if (type === "client") {
      exportParams.clientId = itemId;
    } else if (type === "kind") {
      exportParams.kindId = itemId;
    }

    downloadFile(`/export/orders`, "orders_data.xlsx", exportParams);
  };

  const handleDownloadReport = (order: Order) => {
    downloadFile(
      `/export/order-doc/${order._id}`,
      `invoice-${order.receipt}.pdf`
    );
  };

  // Синхронизация состояний с URL query параметрами
  useEffect(() => {
    const params: Record<string, string | number | boolean | undefined> = {};

    if (searchTerm) params.search = searchTerm;
    if (orderType) params.orderType = orderType;
    if (kind) params.kind = kind;
    if (currency) params.currency = currency;
    if (sortBy !== "date") params.sortBy = sortBy;
    if (sortOrder !== "desc") params.sortOrder = sortOrder;
    if (page !== 1) params.page = page;
    if (pageSize !== 10) params.pageSize = pageSize;
    if (status) params.status = status;
    if (dateRange) {
      params.startDate = dateRange[0]?.format("DD.MM.YYYY");
      params.endDate = dateRange[1]?.format("DD.MM.YYYY");
    }

    queryParams.setParams(params);
  }, [searchTerm, orderType, kind, currency, sortBy, sortOrder, page, pageSize, status, dateRange]);

  useEffect(() => {
    fetchOrders();
  }, [
    page,
    sortBy,
    sortOrder,
    pageSize,
    dateRange,
    orderType,
    kind,
    searchTerm,
    currency,
    type,
    status,
  ]);

  const fetchTaskOrdersForClient = async () => {
    if (type !== "client" || !itemId) return;
    setTaskOrdersLoading(true);
    try {
      const response = await api.get("/task-order", {
        params: {
          clientId: itemId,
          page: 1,
          limit: 1000,
          order: "desc",
        },
      });
      setTaskOrders(response.data.orders || []);
    } catch (error) {
      console.error("Error fetching task orders", error);
    } finally {
      setTaskOrdersLoading(false);
    }
  };

  useEffect(() => {
    fetchTaskOrdersForClient();
  }, [type, itemId]);

  const fetchOrders = async () => {
    setLoading(true);

    try {
      const response = await api.get(`/order/${type}/${itemId}`, {
        params: {
          page,
          limit: pageSize,
          sortBy,
          order: sortOrder,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YYYY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YYYY") : undefined,
          type: orderType,
          kind,
          search: searchTerm,
          currency,
          status,
        },
      });
      setOrders(response.data.orders);
      setTotal(response.data.total);
      setStats(response.data.stats);
    } catch (error) {
      console.error("Error fetching orders", error);
      message.error("Ошибка при получение заказов");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSelectedOrders = () => {
    if (selectedOrders.length === 0) {
      message.warning("Пожалуйста, выберите хотя бы один отчет.");
      return;
    }

    const orderIds = selectedOrders.join(",");

    downloadFile(`/export/order-doc?orderIds=${orderIds}`, `order.pdf`);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleCreateOrder = async (values: any) => {
    setConfirmLoading(true);

    const finalValues = {
      ...values,
      clientId: itemId,
    };

    try {
      await api.post("/order", finalValues);
      message.success("Заказ успешно создан");

      fetchOrders();
      fetchTaskOrdersForClient();
      setIsOrderModalOpen(false);
      setConfirmLoading(false);
      orderForm.resetFields();
      setCreateOrderInitialValues(undefined);
      setCreateOrderDisabledFields([]);
      setCreateOrderTotalMax(undefined);
      setSelectedTaskOrderId(undefined);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating order", error);
      message.error(error.response.data.error);
    }
  };

  const handleDeleteOrder = async (orderId: string) => {
    try {
      await api.delete(`/order/${orderId}`);

      message.success("Заказ успешно удален.");
      fetchOrders();
    } catch (error: any) {
      message.error(error.response.data.error);
    }
  };

  const handleCreateDeliveryExpense = async (values: any) => {
    setConfirmLoading(true);

    try {
      await api.post("/delivery-expense", {
        ...values,
        orderId: selectedOrder?._id,
      });
      message.success("Расходы на доставку успешно добавлены");

      fetchOrders();
      setIsDeliveryExpenseModalOpen(false);
      setConfirmLoading(false);
      deliveryExpenseForm.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating delivery expense", error);
      message.error(error.response.data.error);
    }
  };

  const handleOpenDeliveryExpenseModal = (order: Order) => {
    setSelectedOrder(order);
    setIsDeliveryExpenseModalOpen(true);
  };

  const handleCreateFork = async (values: any) => {
    setConfirmLoading(true);

    try {
      await api.post(`/fork/${selectedOrder?._id}`, values);
      message.success("Откат успешно добавлен");

      fetchOrders();
      setIsForkModalOpen(false);
      setConfirmLoading(false);
      forkForm.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating fork", error);
      message.error(error.response.data.error);
    }
  };

  const handleReviewOrders = async () => {
    setReviewLoading(true);
    try {
      const response = await api.post("/order/review", {
        orderIds: selectedOrders,
      });

      message.success("Проверка заказов успешно завершена");
      fetchOrders();
      setSelectedOrders([]);
    } catch (error) {
      console.error("Error reviewing orders", error);
      message.error("Ошибка при проверке заказов");
    } finally {
      setReviewLoading(false);
    }
  };

  const handleOpenForkModal = (order: Order) => {
    setSelectedOrder(order);
    setIsForkModalOpen(true);
  };

  const activeTaskOrders = taskOrders
    .filter((to) => to.status !== TaskOrderStatus.Rejected)
    .filter((to) => (to.total || 0) - (to.delivered || 0) > 0);

  const getExpectedKgForTaskOrder = (taskOrder: ITaskOrder, quantity: number): number | undefined => {
    const produce: any = Array.isArray(producesList)
      ? producesList.find((p: any) => p._id === taskOrder.produceId)
      : undefined;
    if (!produce) return undefined;

    const totalQuantity =
      typeof produce.totalQuantity === "number"
        ? produce.totalQuantity
        : (Array.isArray(produce.produces)
          ? produce.produces.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0)
          : 0) +
        (Array.isArray(produce.materials)
          ? produce.materials.reduce((sum: number, item: any) => sum + (item.quantity || 0), 0)
          : 0);

    if (!totalQuantity || !quantity) return undefined;
    return roundToThreeDecimals(totalQuantity * quantity);
  };

  const handleOpenSaleFromTaskOrder = (taskOrder: ITaskOrder) => {
    const remaining = (taskOrder.total || 0) - (taskOrder.delivered || 0);
    if (remaining <= 0) {
      message.warning("По этому заказу уже нет остатка для продажи.");
      return;
    }

    const expectedKg = getExpectedKgForTaskOrder(taskOrder, remaining);

    setCreateOrderInitialValues({
      type: OrderType.SALE,
      kind: Kind.PRODUCE,
      // Извлекаем _id из объекта, если produceId является объектом (populated)
      kindId: typeof taskOrder.produceId === 'object' && (taskOrder.produceId as any)?._id
        ? (taskOrder.produceId as any)._id
        : taskOrder.produceId,
      currency: taskOrder.currency,
      price: taskOrder.price,
      discount: taskOrder.discount || 0,
      total: remaining,
      totalKg: expectedKg,
      description: taskOrder.description,
      taskOrderId: taskOrder._id,
      // Автоматически выбираем продажника по владельцу заказа-наряда
      salesUserId: taskOrder.ownerId,
    });
    setCreateOrderDisabledFields(["kind", "kindId", "currency", "price", "discount"]);
    setCreateOrderTotalMax(remaining);
    setIsOrderModalOpen(true);
  };

  const handleOpenTaskOrderSelectModal = () => {
    // Если ранее ничего не выбирали и есть заказы — проставим первый элемент
    if (!selectedTaskOrderId && activeTaskOrders.length > 0) {
      setSelectedTaskOrderId(activeTaskOrders[0]._id);
    }
    setIsTaskOrderSelectModalOpen(true);
  };

  const handleConfirmTaskOrderSelect = () => {
    const to = activeTaskOrders.find((x) => x._id === selectedTaskOrderId);
    if (!to) {
      message.warning("Выберите заказ для продажи.");
      return;
    }
    setIsTaskOrderSelectModalOpen(false);
    handleOpenSaleFromTaskOrder(to);
  };

  useEffect(() => {
    try {
      localStorage.setItem(
        'orders-column-visibility',
        JSON.stringify(visibleColumns)
      );
    } catch (error) {
      console.warn('Failed to save column visibility to localStorage:', error);
    }
  }, [visibleColumns]);

  const handleColumnVisibilityChange = (key: string, checked: boolean) => {
    setVisibleColumns(prev => ({
      ...prev,
      [key]: checked,
    }));
  };

  const handleSelectAllColumns = () => {
    const newVisibility: Record<string, boolean> = {};
    allColumns.forEach(col => {
      if (col.key) {
        newVisibility[col.key] = true;
      }
    });
    setVisibleColumns(newVisibility);
  };

  const handleDeselectAllColumns = () => {
    const newVisibility: Record<string, boolean> = {};
    allColumns.forEach(col => {
      if (col.key) {
        newVisibility[col.key] = false;
      }
    });
    setVisibleColumns(newVisibility);
  };

  const allColumns = [
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
      visible: visibleColumns.date,
    },
    {
      title: "Чек",
      dataIndex: "receipt",
      key: "receipt",
      visible: visibleColumns.receipt,
    },
    {
      title: "Имя клиента",
      dataIndex: "clientName",
      key: "clientName",
      visible: visibleColumns.clientName,
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
      visible: visibleColumns.description,
      render: (description: string) => description || "-",
    },
    {
      title: "Тип",
      dataIndex: "type",
      key: "type",
      visible: visibleColumns.type,
    },
    {
      title: "Тип сущ.",
      dataIndex: "kind",
      key: "kind",
      visible: visibleColumns.kind,
    },
    {
      title: "Имя",
      dataIndex: "name",
      key: "name",
      visible: visibleColumns.name,
    },
    {
      title: "Ед. измерения",
      dataIndex: "unitOfMeasurement",
      key: "unitOfMeasurement",
      visible: visibleColumns.unitOfMeasurement,
    },
    {
      title: "Валюта",
      dataIndex: "currency",
      key: "currency",
      visible: visibleColumns.currency,
    },
    {
      title: "Курс",
      dataIndex: "exchangeRate",
      key: "exchangeRate",
      visible: visibleColumns.exchangeRate,
    },
    {
      title: "Цена в USD",
      key: "price",
      visible: visibleColumns.price,
      render: (_: any, order: Order) => order.price.USD,
    },
    {
      title: "Цена в TJS",
      key: "price",
      visible: visibleColumns.price,
      render: (_: any, order: Order) => order.price.TJS,
    },
    {
      title: "Баланс до",
      key: "balanceBefore",
      visible: visibleColumns.balanceBefore,
      render: (_: any, order: Order) => order.balanceBefore,
    },
    {
      title: "Баланс после",
      key: "balanceAfter",
      visible: visibleColumns.balanceAfter,
      render: (_: any, order: Order) => order.balanceAfter,
    },
    {
      title: "Остаток до",
      dataIndex: "residueBefore",
      key: "residueBefore",
      visible: visibleColumns.residueBefore,
      render: (data: number) => roundToThreeDecimals(data),
    },
    {
      title: "Остаток после",
      dataIndex: "residueAfter",
      key: "residueAfter",
      visible: visibleColumns.residueAfter,
      render: (data: number) => roundToThreeDecimals(data),
    },
    {
      title: "Кол-во",
      dataIndex: "total",
      key: "total",
      visible: visibleColumns.total,
      render: (data: number) => roundToThreeDecimals(data),
    },
    {
      title: "Кол-во (2)",
      dataIndex: "totalKg",
      key: "totalKg",
      visible: visibleColumns.totalKg,
      render: (data: number, record: Order) => {
        if (data === undefined || data === null) return "-";
        const unit = (record as any).residueKgUnit || "кг";
        return `${roundToThreeDecimals(data)} ${unit}`;
      },
    },
    {
      title: "Возврат",
      dataIndex: "returned",
      key: "returned",
      visible: visibleColumns.returned,
    },
    {
      title: "Сумма в USD",
      key: "totalCost",
      visible: visibleColumns.totalCost,
      render: (_: any, order: Order) =>
        roundToThreeDecimals(order.totalCost.USD),
    },
    {
      title: "Сумма в TJS",
      key: "totalCost",
      visible: visibleColumns.totalCost,
      render: (_: any, order: Order) =>
        roundToThreeDecimals(order.totalCost.TJS),
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      visible: visibleColumns.status,
    },
    {
      title: "Создал",
      dataIndex: "ownerName",
      key: "ownerName",
      visible: visibleColumns.ownerName,
    },
    {
      title: "",
      key: "actions",
      fixed: "right" as "right",
      visible: visibleColumns.actions,
      render: (_: any, order: Order) => {
        const menu = (
          <Menu>
            {/* Действия просмотра (GET запросы) */}
            <Menu.Item onClick={() => handleDownloadReport(order)}>
              Получить отчет
            </Menu.Item>

            {order.type === OrderType.PURCHASE && (
              <Menu.Item
                onClick={() =>
                  navigate(
                    `/${order._id}/${encodeURIComponent(order.name)} ${order.receipt
                    }/returns`
                  )
                }
              >
                Возвраты
              </Menu.Item>
            )}

            {order.type === OrderType.SALE && (
              <>
                <Menu.Item
                  onClick={() =>
                    navigate(
                      `/order/sale-returns/${order._id}/${encodeURIComponent(
                        order.name
                      )}/${order.receipt}`
                    )
                  }
                >
                  Возвраты
                </Menu.Item>
                <Menu.Item
                  onClick={() =>
                    navigate(
                      `/forks/${order._id}/${encodeURIComponent(order.name)}`
                    )
                  }
                >
                  Откаты
                </Menu.Item>
              </>
            )}

            {/* Действия редактирования (только для админа) */}
            {canEditOrders(authState.user?.role) && (
              <>
                {order.type === OrderType.PURCHASE && (
                  <Menu.Item
                    onClick={() => handleOpenDeliveryExpenseModal(order)}
                  >
                    Добавить расходы на доставку
                  </Menu.Item>
                )}

                {order.type === OrderType.SALE && (
                  <Menu.Item onClick={() => handleOpenForkModal(order)}>
                    Добавить откат
                  </Menu.Item>
                )}

                <Popconfirm
                  title="Вы уверены, что хотите удалить этот заказ?"
                  onConfirm={() => handleDeleteOrder(order._id)}
                  okText="Да"
                  cancelText="Нет"
                >
                  <Menu.Item>Удалить</Menu.Item>
                </Popconfirm>
              </>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
      hidden: type === "kind",
    },
  ];

  const filteredColumns = allColumns.filter(
    column => column.key && visibleColumns[column.key]
  );

  const columnSelectorContent = (
    <div style={{ width: 300, maxHeight: 400, overflowY: 'auto' }}>
      <div
        style={{
          marginBottom: 16,
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: 8,
        }}
      >
        <Space>
          <Button size='small' onClick={handleSelectAllColumns}>
            Выбрать все
          </Button>
          <Button size='small' onClick={handleDeselectAllColumns}>
            Снять все
          </Button>
        </Space>
      </div>
      {allColumns.map(
        column =>
          column.key && (
            <div key={column.key} style={{ marginBottom: 8 }}>
              <Checkbox
                checked={visibleColumns[column.key]}
                onChange={e =>
                  handleColumnVisibilityChange(column.key, e.target.checked)
                }
              >
                {column.title}
              </Checkbox>
            </div>
          )
      )}
    </div>
  );

  const rowSelection = {
    selectedRowKeys: selectedOrders,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedOrders(selectedRowKeys as string[]);
    },
  };

  const rowClassName = (record: Order) => {
    if (canEditOrders(authState.user?.role) || authState.user?.role === "director") {
      if (record.status === OrderStatus.ACCEPTED) {
        return "row-orange";
      } else if (record.status === OrderStatus.PENDING) {
        return "row-yellow";
      } else if (record.status === OrderStatus.REVIEWED) {
        return "row-green";
      }

      return "";
    } else {
      if (record.status === OrderStatus.ACCEPTED) {
        return "row-green";
      } else if (record.status === OrderStatus.PENDING) {
        return "row-yellow";
      }
      return "";
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title>Заказы {name}</Title>
      {stats && (
        <Row gutter={[16, 16]} justify="start">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card hoverable>
              <Statistic title="Кол-во" value={stats?.totalQuantity} />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <Card hoverable>
              <Statistic
                title="Прибыль в USD"
                value={stats?.totalProfit.USD}
                prefix="$"
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <Card hoverable>
              <Statistic title="Прибыль в TJS" value={stats?.totalProfit.TJS} />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <Card hoverable>
              <Statistic
                title="Заказы общая сумма в TJS"
                value={stats?.totalOrderAmount.TJS}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <Card hoverable>
              <Statistic
                title="Заказы общая сумма в USD"
                value={stats?.totalOrderAmount.USD}
                prefix="$"
              />
            </Card>
          </Col>
        </Row>
      )}
      <Space style={{ marginBottom: 16, marginTop: 16 }} wrap>
        <Search placeholder="Поиск" onSearch={handleSearch} allowClear />
        {type !== "Покупка" && type !== "Продажа" && (
          <Select
            placeholder="Фильтр по типу заказа"
            onChange={(value) => setOrderType(value)}
            allowClear
            style={{ width: 200 }}
          >
            {Object.values(OrderType).map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
        )}
        {type === "client" && (
          <Select
            placeholder="Фильтр по типу сущ."
            onChange={(value) => setKind(value)}
            allowClear
            style={{ width: 200 }}
          >
            {Object.values(Kind).map((type) => (
              <Option key={type} value={type}>
                {type}
              </Option>
            ))}
          </Select>
        )}
        <Select
          placeholder="Фильтр по статусу"
          onChange={(value) => setStatus(value)}
          allowClear
          style={{ width: 200 }}
        >
          {Object.values(OrderStatus).map((status) => (
            <Option key={status} value={status}>
              {status}
            </Option>
          ))}
        </Select>
        <Select
          placeholder="Сортировать по"
          defaultValue="date"
          onChange={(value) => setSortBy(value)}
          style={{ width: 200 }}
        >
          <Option value="date">Дата</Option>
          {type === "kind" && <Option value="clientName">Имя клиента</Option>}
          <Option value="price">Цена</Option>
          <Option value="total">Кол-во</Option>
          <Option value="totalCost">Сумма</Option>
          <Option value="profit">Профит</Option>
          <Option value="totalPurchased">Куплено</Option>
        </Select>
        <Select
          defaultValue="desc"
          onChange={(value: "asc" | "desc") => setSortOrder(value)}
          style={{ width: 150 }}
        >
          <Option value="asc">По возрастанию</Option>
          <Option value="desc">По убыванию</Option>
        </Select>

        <RangePicker
          onChange={handleRangeChange}
          placeholder={["Начало", "Конец"]}
          format="DD.MM.YYYY"
          value={dateRange}
        />

        {type === "client" && canEditOrders(authState.user?.role) && (
          <Button
            type="primary"
            onClick={() => {
              setCreateOrderInitialValues(undefined);
              setCreateOrderDisabledFields([]);
              setCreateOrderTotalMax(undefined);
              setSelectedTaskOrderId(undefined);
              setIsOrderModalOpen(true);
            }}
          >
            Добавить
          </Button>
        )}

        <Button
          type="primary"
          loading={downloadLoading}
          onClick={handleDownloadSelectedOrders}
          disabled={selectedOrders.length === 0}
        >
          Получить отчет
        </Button>

        {canEditOrders(authState.user?.role) && (
          <Button
            type="primary"
            loading={reviewLoading}
            onClick={handleReviewOrders}
            disabled={selectedOrders.length === 0}
          >
            Проверка Админ
          </Button>
        )}

        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>

        <Popover
          content={columnSelectorContent}
          title='Выбор колонок'
          trigger='click'
          placement='bottomLeft'
        >
          <Button icon={<SettingOutlined />}>Колонки</Button>
        </Popover>
      </Space>
      <Table
        columns={filteredColumns}
        dataSource={orders}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
        loading={loading}
        rowSelection={rowSelection}
        rowClassName={rowClassName}
      />
      {type === "client" && (
        <>
          <CreateOrder
            title="Создать заказ"
            open={isOrderModalOpen}
            form={orderForm}
            confirmLoading={confirmLoading}
            onCancel={() => setIsOrderModalOpen(false)}
            onOk={() => orderForm.submit()}
            onFinish={handleCreateOrder}
            clientType={clientType || ""}
            initialValues={createOrderInitialValues}
            disabledFields={createOrderDisabledFields}
            totalMax={createOrderTotalMax}
          />
        </>
      )}

      <Modal
        title="Выберите заказ для продажи"
        open={isTaskOrderSelectModalOpen}
        onCancel={() => setIsTaskOrderSelectModalOpen(false)}
        onOk={handleConfirmTaskOrderSelect}
        okText="Далее"
        cancelText="Отмена"
        confirmLoading={taskOrdersLoading}
        okButtonProps={{ disabled: activeTaskOrders.length === 0 || !selectedTaskOrderId }}
      >
        {activeTaskOrders.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            <p>Нет активных заказов для продажи по этому клиенту.</p>
            <p style={{ fontSize: '12px', marginTop: '8px' }}>
              Создайте заказ для клиента, чтобы продавать по нему.
            </p>
          </div>
        ) : (
          <Select
            placeholder="Заказ для продажи"
            style={{ width: "100%" }}
            loading={taskOrdersLoading}
            value={selectedTaskOrderId}
            allowClear
            onChange={(value) => setSelectedTaskOrderId(value)}
            optionFilterProp="children"
            showSearch
          >
            {activeTaskOrders.map((to) => {
              const remaining = (to.total || 0) - (to.delivered || 0);
              return (
                <Option key={to._id} value={to._id}>
                  {to.produceName} — остаток: {roundToThreeDecimals(remaining)} — {to.price}{" "}
                  {to.currency} — скидка: {to.discount || 0}%
                </Option>
              );
            })}
          </Select>
        )}
      </Modal>
      {selectedOrder && (
        <>
          <CreateDeliveryExpense
            title="Добавить расходы на доставку"
            open={isDeliveryExpenseModalOpen}
            form={deliveryExpenseForm}
            confirmLoading={confirmLoading}
            onCancel={() => setIsDeliveryExpenseModalOpen(false)}
            onOk={() => deliveryExpenseForm.submit()}
            onFinish={handleCreateDeliveryExpense}
            order={selectedOrder}
          />
          <CreateFork
            title="Добавить откат"
            open={isForkModalOpen}
            form={forkForm}
            confirmLoading={confirmLoading}
            onCancel={() => setIsForkModalOpen(false)}
            onOk={() => forkForm.submit()}
            onFinish={handleCreateFork}
            order={selectedOrder}
          />
        </>
      )}
    </div>
  );
}
